import { Link, useLocation } from "react-router-dom";
import { useSinglePrismicDocument } from "@prismicio/react";
import { useState, useEffect, useCallback } from "react";
import { isPrismicConfigured } from "../../prismic";
import { HiMenu } from "react-icons/hi";

const Navbar = () => {
  // Always call the hook, but handle the result conditionally
  const [document, { state }] = useSinglePrismicDocument("navigation");
  const logo = (isPrismicConfigured() && document?.data?.logo?.url) 
    ? document.data.logo.url 
    : "/img/logo-andycary-light.png";
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState('up');
  const [lastScroll, setLastScroll] = useState(0);
  const location = useLocation();

  // Check if we're on the blog page
  const isBlogPage = location.pathname.startsWith('/blog');

  const handleScroll = useCallback(() => {
    const currentScroll = window.scrollY;
    
    // Determine scroll direction
    if (currentScroll > lastScroll && currentScroll > 50) {
      setScrollDirection('down');
    } else {
      setScrollDirection('up');
    }
    
    // Set scrolled state
    if (currentScroll > 50) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
    
    setLastScroll(currentScroll);
  }, [lastScroll]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const toggleMenu = () => setIsOpen(!isOpen);

  // Helper function to check if a link is active
  const isActiveLink = (path) => {
    if (path === '/blog') {
      return location.pathname.startsWith('/blog');
    }
    return location.pathname === path;
  };

  // Don't show loading state if Prismic is not configured
  if (state === "loading" && isPrismicConfigured()) {
    return (
      <header className={`header-transparent ${scrolled ? 'scrolled' : ''} ${isBlogPage ? 'blog-theme' : ''}`}>
        <nav className="flex items-center justify-between p-4">
          <div className="container mx-auto px-4">
            <span className="navbar-brand">Loading...</span>
          </div>
        </nav>
      </header>
    );
  }

  return (
    <header className={`header-transparent 
      ${scrolled ? 'scrolled' : ''} 
      ${scrollDirection === 'down' ? 'scroll-down' : 'scroll-up'}
      ${isBlogPage ? 'blog-theme' : ''}`}
    >
      <nav className="navbar" role="navigation">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="navbar-brand" aria-label="Home">
              <img src={logo} className="brand-logo" alt="Site Logo" />
            </Link>
            
            {/* Mobile Menu Button */}
            <button
              className="navbar-toggler lg:hidden"
              type="button"
              onClick={toggleMenu}
              aria-controls="navbarSupportedContent"
              aria-expanded={isOpen}
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon">
                <HiMenu className="w-6 h-6" />
              </span>
            </button>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:items-center lg:justify-end desktop-nav" role="menubar">
              <ul className="flex lg:flex-row lg:ml-auto lg:space-x-6">
                <li className="nav-item" role="none">
                  <Link 
                    to="/about" 
                    className={`nav-link desktop-nav-link ${isActiveLink('/about') ? 'active' : ''}`} 
                    role="menuitem"
                  >
                    About
                  </Link>
                </li>
                <li className="nav-item" role="none">
                  <Link 
                    to="/portfolio" 
                    className={`nav-link desktop-nav-link ${isActiveLink('/portfolio') ? 'active' : ''}`} 
                    role="menuitem"
                  >
                    Portfolio
                  </Link>
                </li>
                <li className="nav-item" role="none">
                  <Link 
                    to="/blog" 
                    className={`nav-link desktop-nav-link ${isActiveLink('/blog') ? 'active' : ''}`} 
                    role="menuitem"
                  >
                    Blog
                  </Link>
                </li>

              </ul>
            </div>
          </div>
          
          {/* Mobile Navigation Menu */}
          <div
            className={`navbar-collapse ${isOpen ? 'show' : ''} lg:hidden`}
            id="navbarSupportedContent"
          >
            <ul className="flex flex-col space-y-0 mobile-nav-list" role="menubar">
              <li className="nav-item" role="none">
                <Link 
                  to="/about" 
                  className={`nav-link mobile-nav-link ${isActiveLink('/about') ? 'active' : ''}`} 
                  role="menuitem"
                  onClick={() => setIsOpen(false)}
                >
                  About
                </Link>
              </li>
              <li className="nav-item" role="none">
                <Link 
                  to="/portfolio" 
                  className={`nav-link mobile-nav-link ${isActiveLink('/portfolio') ? 'active' : ''}`} 
                  role="menuitem"
                  onClick={() => setIsOpen(false)}
                >
                  Portfolio
                </Link>
              </li>
              <li className="nav-item" role="none">
                <Link 
                  to="/blog" 
                  className={`nav-link mobile-nav-link ${isActiveLink('/blog') ? 'active' : ''}`} 
                  role="menuitem"
                  onClick={() => setIsOpen(false)}
                >
                  Blog
                </Link>
              </li>

            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
